import { z } from "zod";

import { BRAND_MARKUP } from "../../constants";
import { ValidationError } from "../../errors";
import { attachment } from "../shared";
import { defineModel, KieNanoBanana2CreateTaskPayload } from "../types";

// 9:16 is first to be the default in sandbox, auto is last
// Nano Banana 2 supports extended aspect ratios including 4:1, 1:4, 8:1, 1:8
const ASPECT_RATIO_VALUES = [
  "9:16",
  "16:9",
  "1:1",
  "4:5",
  "3:4",
  "2:3",
  "3:2",
  "4:3",
  "5:4",
  "21:9",
  "4:1",
  "1:4",
  "8:1",
  "1:8",
  "auto",
] as const;

const RESOLUTION_VALUES = ["1K", "2K", "4K"] as const;

type AspectRatio = (typeof ASPECT_RATIO_VALUES)[number];
type Resolution = (typeof RESOLUTION_VALUES)[number];

const ASPECT_RATIO_OPTIONS: readonly {
  label: string;
  value: AspectRatio;
}[] = ASPECT_RATIO_VALUES.map((value) => ({
  label: value === "auto" ? "Auto" : value,
  value,
}));

const RESOLUTION_OPTIONS: readonly {
  label: string;
  value: Resolution;
}[] = RESOLUTION_VALUES.map((value) => ({
  label: value,
  value,
}));

// Runware dimension mappings for google:4@3 (Nano Banana 2 / Gemini 3.1 Flash Image)
// Maps resolution and aspect ratio to explicit width/height as required by the API
const RUNWARE_DIMENSION_MAP: Record<
  Resolution,
  Record<Exclude<AspectRatio, "auto">, { width: number; height: number }>
> = {
  "1K": {
    "1:1": { width: 1024, height: 1024 },
    "3:2": { width: 1264, height: 848 },
    "2:3": { width: 848, height: 1264 },
    "4:3": { width: 1200, height: 896 },
    "3:4": { width: 896, height: 1200 },
    "4:5": { width: 928, height: 1152 },
    "5:4": { width: 1152, height: 928 },
    "9:16": { width: 768, height: 1376 },
    "16:9": { width: 1376, height: 768 },
    "21:9": { width: 1584, height: 672 },
    "4:1": { width: 2048, height: 512 },
    "1:4": { width: 512, height: 2048 },
    "8:1": { width: 3072, height: 384 },
    "1:8": { width: 384, height: 3072 },
  },
  "2K": {
    "1:1": { width: 2048, height: 2048 },
    "3:2": { width: 2528, height: 1696 },
    "2:3": { width: 1696, height: 2528 },
    "4:3": { width: 2400, height: 1792 },
    "3:4": { width: 1792, height: 2400 },
    "4:5": { width: 1856, height: 2304 },
    "5:4": { width: 2304, height: 1856 },
    "9:16": { width: 1536, height: 2752 },
    "16:9": { width: 2752, height: 1536 },
    "21:9": { width: 3168, height: 1344 },
    "4:1": { width: 4096, height: 1024 },
    "1:4": { width: 1024, height: 4096 },
    "8:1": { width: 6144, height: 768 },
    "1:8": { width: 768, height: 6144 },
  },
  "4K": {
    "1:1": { width: 4096, height: 4096 },
    "3:2": { width: 5056, height: 3392 },
    "2:3": { width: 3392, height: 5056 },
    "4:3": { width: 4800, height: 3584 },
    "3:4": { width: 3584, height: 4800 },
    "4:5": { width: 3712, height: 4608 },
    "5:4": { width: 4608, height: 3712 },
    "9:16": { width: 3072, height: 5504 },
    "16:9": { width: 5504, height: 3072 },
    "21:9": { width: 6336, height: 2688 },
    "4:1": { width: 8192, height: 2048 },
    "1:4": { width: 2048, height: 8192 },
    "8:1": { width: 12288, height: 1536 },
    "1:8": { width: 1536, height: 12288 },
  },
};

// Runware supports up to 14 reference images
const ATTACHMENT_SLOTS = Array.from({ length: 14 }, (_, index) => ({
  type: "image" as const,
  allowedFormats: ["image/jpeg", "image/png", "image/webp"],
  label: `Image ${index + 1}`,
  slot: `reference_image_${index + 1}`,
  group: "reference",
  required: false,
}));

// Tiered pricing per resolution (USD)
const PRICE_PER_RESOLUTION_USD: Record<Resolution, number> = {
  "1K": 0.06895,
  "2K": 0.10255,
  "4K": 0.15295,
};

// Per input/reference image surcharge (USD)
const PRICE_PER_INPUT_IMAGE_USD = 0.00028;

const nanoBanana2Params = z
  .object({
    prompt: z.string().default(""),
    aspectRatio: z.enum(ASPECT_RATIO_VALUES).default("9:16"),
    resolution: z.enum(RESOLUTION_VALUES).default("1K"),
    attachments: z.array(attachment).optional(),
    styleId: z.string().uuid().optional().nullable(),
  })
  .strip();

export const nanoBanana2 = defineModel({
  key: "nano-banana-2",
  stepType: "image",
  spec: {
    label: "Nano Banana 2",
    description:
      "Fast, high-quality image generation with multi-character consistency and accurate text rendering",
    icon: "nanobanana-icon.svg",
    tags: ["image-gen", "image-edit"],
    featured: true,
    features: {
      aspectRatio: true,
      resolution: true,
      attachments: true,
      prompts: true,
      variations: true,
    } as const,
    options: {
      aspectRatio: { items: ASPECT_RATIO_OPTIONS },
      resolution: { items: RESOLUTION_OPTIONS },
      attachments: ATTACHMENT_SLOTS,
    },
    params: nanoBanana2Params,
  },
  referencePricing: {
    provider: "fal.ai",
    url: "https://fal.ai/models/fal-ai/nano-banana-2",
    entries: [
      { unit: "per_image", unitPriceMicrodollars: 80_000, variant: "1K" },
      { unit: "per_image", unitPriceMicrodollars: 120_000, variant: "2K" },
      { unit: "per_image", unitPriceMicrodollars: 160_000, variant: "4K" },
    ],
  },
  providers: [
    {
      provider: "runware",
      modelId: "google:4@3",
      estimateCost(params) {
        const resolution = params.resolution ?? "1K";
        const basePriceUsd = PRICE_PER_RESOLUTION_USD[resolution];

        // Count reference images for surcharge
        const referenceImageCount =
          params.attachments?.filter((file) =>
            file.slot.startsWith("reference_image_"),
          ).length ?? 0;
        const inputImageSurchargeUsd =
          referenceImageCount * PRICE_PER_INPUT_IMAGE_USD;

        const totalCostUsd = basePriceUsd + inputImageSurchargeUsd;
        return Math.ceil(totalCostUsd * 1_000_000 * (1 + BRAND_MARKUP));
      },
      toPayload(params) {
        if (!params?.prompt?.length) {
          throw new ValidationError(
            "Please enter a prompt to generate an image.",
          );
        }

        const referenceImages =
          params.attachments
            ?.filter((file) => file.slot.startsWith("reference_image_"))
            .map((file) => file.data ?? file.url)
            .filter((url): url is string => Boolean(url)) ?? [];

        const hasReferenceImages = referenceImages.length > 0;

        // For "auto" aspect ratio:
        // - With reference images: omit width/height, use resolution param (Runware infers from first image)
        // - Without reference images: default to 1:1 since text-to-image requires explicit dimensions
        const effectiveAspectRatio =
          params.aspectRatio === "auto" && !hasReferenceImages
            ? "1:1"
            : params.aspectRatio;

        // For image-to-image with "auto", omit dimensions and let Runware infer from reference
        const shouldOmitDimensions =
          params.aspectRatio === "auto" && hasReferenceImages;

        const dimensions = shouldOmitDimensions
          ? {}
          : RUNWARE_DIMENSION_MAP[params.resolution][
              effectiveAspectRatio as Exclude<AspectRatio, "auto">
            ];

        return {
          model: this.modelId,
          positivePrompt: params.prompt,
          ...dimensions,
          ...(hasReferenceImages ? { referenceImages } : {}),
          // When omitting dimensions for image-to-image, pass resolution as lowercase
          ...(shouldOmitDimensions
            ? { resolution: params.resolution.toLowerCase() }
            : {}),
        };
      },
    },
    {
      provider: "kie",
      modelId: "nano-banana-2",
      estimateCost(params) {
        const resolution = params.resolution ?? "1K";
        const basePriceUsd = PRICE_PER_RESOLUTION_USD[resolution];

        const referenceImageCount =
          params.attachments?.filter((file) =>
            file.slot.startsWith("reference_image_"),
          ).length ?? 0;
        const inputImageSurchargeUsd =
          referenceImageCount * PRICE_PER_INPUT_IMAGE_USD;

        const totalCostUsd = basePriceUsd + inputImageSurchargeUsd;
        return Math.ceil(totalCostUsd * 1_000_000 * (1 + BRAND_MARKUP));
      },
      toPayload(params): KieNanoBanana2CreateTaskPayload {
        if (!params?.prompt?.length) {
          throw new ValidationError(
            "Please enter a prompt to generate an image.",
          );
        }

        const imageUrls =
          params.attachments
            ?.filter((file) => file.slot.startsWith("reference_image_"))
            .map((file) => file.data ?? file.url)
            .filter((url): url is string => Boolean(url)) ?? [];

        return {
          model: "nano-banana-2",
          input: {
            prompt: params.prompt,
            // "auto" means don't send aspect_ratio, let Kie decide
            ...(params.aspectRatio !== "auto"
              ? { aspect_ratio: params.aspectRatio }
              : {}),
            resolution: params.resolution,
            output_format: "png",
            ...(imageUrls.length > 0 ? { image_input: imageUrls } : {}),
          },
        };
      },
    },
  ],
});
